/**
 * Combo field of «Зафиксировать» (design QuickCapture.dc.html `combo`): type to filter the first 7
 * matches, «Создать «…»» adds a new record, Enter picks the highlighted option, ↑/↓ move, Esc closes
 * the list only. The previous value stays until a pick; clearing the text clears it. The list is
 * placed fixed under the field (above it when there is no room below), so the dialog's scrolling body
 * does not cut it (V163).
 */
import { NEW_REF_PREFIX } from '@outsource/shared';
import { useId, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent, Ref, RefObject } from 'react';
import { cn } from '../../lib/cn';
import type { ComboItem, CreateKind, FieldSpec } from './captureFields';

const MAX_MATCHES = 7;
const BLUR_CLOSE_MS = 120;
const CREATE_SUB: Readonly<Record<CreateKind, string>> = {
  person: 'новый человек',
  project: 'новый проект',
  client: 'новый клиент',
};

type ComboSpec = Extract<FieldSpec, { kind: 'combo' }>;

const LIST_MAX_PX = 240;
const LIST_GAP_PX = 4;
const VIEWPORT_MARGIN_PX = 8;

/** Fixed position of the list for the field's box: below it, or above when it does not fit. */
function listPlacement(box: DOMRect): CSSProperties {
  const below = window.innerHeight - box.bottom - VIEWPORT_MARGIN_PX;
  const isAbove = below < LIST_MAX_PX && box.top > below;
  const room = Math.max(0, (isAbove ? box.top - VIEWPORT_MARGIN_PX : below) - LIST_GAP_PX);
  return {
    position: 'fixed',
    left: box.left,
    width: box.width,
    maxHeight: Math.min(LIST_MAX_PX, room),
    ...(isAbove ? { bottom: window.innerHeight - box.top + LIST_GAP_PX } : { top: box.bottom + LIST_GAP_PX }),
  };
}

/** The list's place, kept in step with layout, scrolling and resizing while it is open. */
function useListPlacement(isOpen: boolean): { readonly boxRef: RefObject<HTMLDivElement | null>; readonly style: CSSProperties | undefined } {
  const boxRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<CSSProperties | undefined>(undefined);
  const place = () => {
    const box = boxRef.current?.getBoundingClientRect();
    if (!box) return;
    const next = listPlacement(box);
    setStyle((current) => (JSON.stringify(current) === JSON.stringify(next) ? current : next));
  };
  // The dialog may still be settling when the list opens: measure after every render.
  useLayoutEffect(() => {
    if (isOpen) place();
  });
  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [isOpen]);
  return { boxRef, style };
}

interface Option extends ComboItem {
  readonly create: boolean;
}

interface ComboFieldProps {
  readonly id: string;
  readonly spec: ComboSpec;
  readonly value: string;
  readonly onChange: (patch: Record<string, string>) => void;
  readonly inputRef?: Ref<HTMLInputElement>;
}

function optionsFor(spec: ComboSpec, query: string | null): Option[] {
  const q = (query ?? '').trim().toLowerCase();
  const matches = spec.items
    .filter((i) => !q || `${i.label} ${i.sub}`.toLowerCase().includes(q))
    .slice(0, MAX_MATCHES);
  const options: Option[] = matches.map((i) => ({ ...i, create: false }));
  const exists = spec.items.some((i) => i.label.toLowerCase() === q);
  if (spec.create && q && !exists) {
    const name = (query ?? '').trim();
    options.push({
      id: `${NEW_REF_PREFIX}${name}`,
      label: `Создать «${name}»`,
      sub: CREATE_SUB[spec.create],
      create: true,
    });
  }
  return options;
}

function shownText(spec: ComboSpec, value: string, query: string | null): string {
  if (query != null) return query;
  if (value.startsWith(NEW_REF_PREFIX)) return value.slice(NEW_REF_PREFIX.length);
  return spec.items.find((i) => i.id === value)?.label ?? '';
}

export function ComboField({ id, spec, value, onChange, inputRef }: ComboFieldProps) {
  const [query, setQuery] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const listId = useId();
  const blurTimer = useRef<number | undefined>(undefined);
  const options = optionsFor(spec, query);
  const typed = !!query?.trim();
  const highlighted = typed || active > 0 ? Math.min(active, options.length - 1) : -1;
  const isOpen = open && options.length > 0;
  const { boxRef, style: listStyle } = useListPlacement(isOpen);

  const pick = (option: Option | undefined) => {
    if (!option) return;
    onChange({ [spec.key]: option.id, ...(spec.onPick?.(option.id) ?? {}) });
    setQuery(null);
    setOpen(false);
    setActive(0);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !(event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      pick(options[Math.max(0, highlighted)]);
    } else if (event.key === 'Escape' && isOpen) {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((Math.max(0, highlighted) + step + options.length) % Math.max(1, options.length));
    }
  };

  return (
    <div ref={boxRef} className="relative">
      <input
        id={id}
        ref={inputRef}
        value={shownText(spec, value, query)}
        placeholder={spec.placeholder ?? 'Начните вводить'}
        autoComplete="off"
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={isOpen && highlighted >= 0 ? `${listId}-${highlighted}` : undefined}
        onChange={(event) => {
          const text = event.target.value;
          setQuery(text);
          setOpen(true);
          setActive(0);
          if (!text) onChange({ [spec.key]: '' });
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          window.clearTimeout(blurTimer.current);
          blurTimer.current = window.setTimeout(() => setOpen(false), BLUR_CLOSE_MS);
        }}
        onKeyDown={onKeyDown}
        className={cn(
          'box-border h-[34px] w-full rounded-2 border bg-bg-3 px-2.5 text-[13px] text-fg-1',
          isOpen ? 'border-pi-navy' : 'border-border-1',
        )}
      />
      {value.startsWith(NEW_REF_PREFIX) && (
        <span className="absolute right-2 top-[7px] h-5 rounded-pill bg-info-bg px-2 text-[11px] font-semibold leading-5 text-info-fg">
          новое
        </span>
      )}
      {isOpen && (
        <div
          id={listId}
          role="listbox"
          aria-label={spec.label}
          style={listStyle}
          className="z-[60] overflow-y-auto rounded-2 border border-border-1 bg-bg-3 p-1 shadow-md"
        >
          {options.map((option, index) => (
            <div
              key={option.id}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === highlighted}
              onMouseDown={(event) => {
                event.preventDefault();
                pick(option);
              }}
              className={cn(
                'flex h-[34px] cursor-pointer items-center gap-2 rounded-ctl px-2.5 hover:bg-bg-1',
                index === highlighted || (option.create && options.length === 1)
                  ? 'bg-bg-1'
                  : 'bg-transparent',
              )}
            >
              <span className={cn('truncate', option.create ? 'font-semibold' : 'font-medium')}>
                {option.label}
              </span>
              <span className="truncate text-fg-3">{option.sub}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
